import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginPage } from './LoginPage';
import { apiRequest } from './msalInstance';
import { clearSignedOut, wasSignedOut } from './session';

/**
 * Renders its children only for a signed-in user; everybody else gets the login page. Nobody is redirected to
 * Microsoft for arriving - an application that bounces every visitor to an identity provider cannot be looked
 * at at all.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (isAuthenticated) {
    return <>{children}</>;
  }

  // On the way to Microsoft, or on the way back: a button to press would be a second redirect.
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="centered-message">
        <img src="/images/kersia_logo.svg" alt="Kersia" className="signin-logo" />
        <p>
          <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
          {t('app.signingIn')}
        </p>
      </div>
    );
  }

  return (
    <LoginPage
      // Set by signing out, and read here only to say so. See session.ts.
      disconnected={wasSignedOut()}
      onSignIn={() => {
        // Cleared first: coming back signed in, they are not somebody who just disconnected.
        clearSignedOut();
        void instance.loginRedirect(apiRequest);
      }}
    />
  );
}
